if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.mobileOnly = this.dataset.mobileOnly === "true";
      this.pauseOutsideViewport = this.dataset.pauseOutsideViewport === "true";
      this.restartGifs = this.dataset.restartGifs === "true";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.mobileMediaQuery = window.matchMedia("(max-width: 767px)");
      this.handleViewportChange = this.handleViewportChange.bind(this);

      if (!this.mobileOnly || this.mobileMediaQuery.matches) {
        this.initSlider();
      }

      if (this.mobileOnly) {
        this.mobileMediaQuery.addEventListener("change", this.handleViewportChange);
      }
    }

    disconnectedCallback() {
      if (this.mobileOnly) {
        this.mobileMediaQuery.removeEventListener("change", this.handleViewportChange);
      }
      if (this.autoplayObserver) {
        this.autoplayObserver.disconnect();
      }
    }

    handleViewportChange(event) {
      if (event.matches && !this.slider) {
        this.initSlider();
      }
    }

    initSlider() {
      if (this.slider) return;

      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: true,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 1000,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider.init();

      if (this.pauseOutsideViewport || this.restartGifs) {
        this.initViewportObserver();
      }

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
      }
    }

    initViewportObserver() {
      if (this.autoplay && this.pauseOutsideViewport && this.slider.autoplay) {
        this.slider.autoplay.stop();
      }

      if (!("IntersectionObserver" in window)) {
        this.isInViewport = true;
        if (this.restartGifs) this.restartActiveGifs();
        if (this.autoplay && this.pauseOutsideViewport && this.slider.autoplay) {
          this.slider.autoplay.start();
        }
        return;
      }

      this.autoplayObserver = new IntersectionObserver(
        ([entry]) => {
          const isInViewport = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          const hasEnteredViewport = isInViewport && !this.isInViewport;
          this.isInViewport = isInViewport;

          if (hasEnteredViewport && this.restartGifs) {
            this.restartActiveGifs();
          }

          if (this.autoplay && this.pauseOutsideViewport && this.slider.autoplay) {
            if (isInViewport) {
              this.slider.autoplay.start();
            } else {
              this.slider.autoplay.stop();
            }
          }
        },
        { threshold: [0, 0.25] }
      );
      this.autoplayObserver.observe(this);
    }

    restartActiveGifs(swiper = this.slider) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      activeSlide.querySelectorAll("img").forEach((image, index) => {
        const imageSources = [image.currentSrc, image.src, image.srcset].filter(Boolean).join(" ");
        if (!/\.gif(?:[?#\s]|$)/i.test(imageSources)) return;

        const source = image.currentSrc || image.src;
        const restartToken = `${Date.now()}-${index}`;
        const replacement = image.cloneNode(true);
        let restartSource = source;

        image.dataset.gifRestartToken = restartToken;
        replacement.removeAttribute("src");
        replacement.removeAttribute("srcset");
        replacement.removeAttribute("sizes");
        replacement.loading = "eager";

        try {
          const restartUrl = new URL(source, window.location.href);
          restartUrl.searchParams.set("_gif_restart", restartToken);
          restartSource = restartUrl.href;
        } catch (error) {
          // Use the original source when the URL cannot be parsed.
        }

        replacement.onload = async () => {
          try {
            if (replacement.decode) await replacement.decode();
          } catch (error) {
            // The load event still confirms there is an image ready to display.
          }

          const isStillActive = swiper.slides[swiper.activeIndex] === activeSlide;
          const canReplace = !this.pauseOutsideViewport || this.isInViewport;
          if (
            image.isConnected &&
            image.dataset.gifRestartToken === restartToken &&
            isStillActive &&
            canReplace
          ) {
            replacement.onload = null;
            replacement.onerror = null;
            image.replaceWith(replacement);
          }
        };

        replacement.onerror = () => {
          replacement.onload = null;
          replacement.onerror = null;
        };
        replacement.src = restartSource;
      });
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);

      if (this.restartGifs && (!this.pauseOutsideViewport || this.isInViewport)) {
        this.restartActiveGifs(swiper);
      }

      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
